import * as React from 'react';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { appConfigStore } from 'Models/AppConfigStore';
import { configuredHexColour } from 'Util/_HumanWritten/ColourUtils';
import { store } from 'Models/Store';
import { getParkingLocations, ParkingLocationDto } from 'Services/Api/_HumanWritten/ParkingService';
import { getPendingParkingPurchase, clearPendingParkingPurchase } from '../PurchaseParking/ParkingPurchasePendingFlag';
import { useSearchParams } from 'Hooks/useSearchParams';
import Icon from '../Icon/Icon';

const PurchaseParkingModal = React.lazy(
	() => import('Views/Components/_HumanWritten/PurchaseParking/PurchaseParkingModal'),
);

function ParkingButton() {
	const [showParkingModal, setShowParkingModal] = useState(false);
	const [hasAvailableLocations, setHasAvailableLocations] = useState<boolean | null>(null);
	const { getSearchParam, deleteSearchParam } = useSearchParams();

	useEffect(() => {
		if (!appConfigStore.parkingEnabled) return;

		getParkingLocations()
			.then(locations => {
				const available = locations.some(
					(l: ParkingLocationDto) => !l.disabled && l.hasCapacityRestrictions && l.hasActivePricing,
				);
				setHasAvailableLocations(available);

				// Auto-open modal if user was redirected back after registration
				if (available && getPendingParkingPurchase()) {
					clearPendingParkingPurchase();
					setShowParkingModal(true);
				}

				// Auto-open modal if ?parking=true query param is present
				if (available && getSearchParam('parking') === 'true') {
					deleteSearchParam('parking');
					setShowParkingModal(true);
				}
			})
			.catch(() => {
				setHasAvailableLocations(false);
			});
	}, []);

	if (!appConfigStore.parkingEnabled || hasAvailableLocations !== true) {
		return null;
	}

	// Hide for customers if customer parking creation is disabled
	if (!store.isStaff && !appConfigStore.parkingCustomerCanCreate) {
		return null;
	}

	// Parking for a customer follows BookOnBehalfOfCustomer, like every other booking.
	if (!store.mayMakeBookings) {
		return null;
	}

	return (
		<>
			<div className="begin-booking__divider" />
			<div
				className="begin-booking__parking-link"
				// A custom property rather than an inline background on the icon itself, so the
				// stylesheet keeps ownership of the tile's size, shape and responsive sizing and only
				// the fill is configurable.
				// The configured fill, or undefined when it is unset or not a hex value — in which
				// case the stylesheet's own translucent fallback stands. Same "blank means leave it
				// alone" contract as the voucher and anytime ticket card colours on this page.
				style={
					{
						'--parking-icon-colour': configuredHexColour(appConfigStore.parkingHomepageIconColour),
					} as React.CSSProperties
				}
				data-test-id="home-book-parking-button"
				onClick={() => setShowParkingModal(true)}
				role="button"
				tabIndex={0}
				onKeyDown={e => {
					if (e.key === 'Enter') setShowParkingModal(true);
				}}
			>
				<div className="begin-booking__parking-left">
					<Icon
						name={appConfigStore.parkingIconName}
						classname="begin-booking__parking-icon"
						useMaterialIcons
					/>
					<div className="begin-booking__parking-text">
						<span className="begin-booking__parking-label">
							<FormattedMessage id="homepage__parking__title" defaultMessage="Book parking" />
						</span>
						<span className="begin-booking__parking-subtitle">
							<FormattedMessage
								id="homepage__parking__subtitle"
								defaultMessage="Reserve a spot for your vehicle"
							/>
						</span>
					</div>
				</div>
				<Icon name="arrow_forward" classname="begin-booking__parking-arrow" useMaterialIcons />
			</div>
			{showParkingModal && (
				<React.Suspense fallback={null}>
					<PurchaseParkingModal isOpen={showParkingModal} onClose={() => setShowParkingModal(false)} />
				</React.Suspense>
			)}
		</>
	);
}

export default ParkingButton;
